import axios from 'axios';
import { API_BASE_OVERRIDE } from './apiBase';
import { createSendTxByEmail } from './api';

// P2P API Base URL - use localhost:5000 in development, production URL otherwise
const P2P_BASE_URL = API_BASE_OVERRIDE
  ? `${API_BASE_OVERRIDE}/api/v1/p2p`
  : 'https://api.v1.indexx.ai/api/v1/p2p';

console.log('🔗 P2P API Base URL:', P2P_BASE_URL);

// Create P2P API instance
const P2P_API = axios.create({
  baseURL: P2P_BASE_URL,
});

// Add request interceptor to include auth token
P2P_API.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for better error handling
P2P_API.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.code === 'ECONNREFUSED') {
      error.message = 'Cannot connect to backend server. Please ensure it\'s running on localhost:5000';
    } else if (error.response?.status === 401) {
      error.message = 'Authentication required. Please log in.';
    } else if (error.response?.status === 404) {
      error.message = 'P2P endpoints not found. Please check backend configuration.';
    } else if (error.response?.status >= 500) {
      error.message = 'Backend server error. Please try again later.';
    }
    return Promise.reject(error);
  }
);

// Health Check Function
export const checkBackendHealth = async () => {
  try {
    // Check offers endpoint since health endpoint doesn't exist
    const response = await P2P_API.get('/offers');
    return {
      status: 'connected',
      message: 'P2P backend is running',
      offersCount: response.data.data?.length || 0
    };
  } catch (error: any) {
    console.error('Backend health check failed:', error);
    
    // If it's a 404, the backend is running but P2P endpoints aren't implemented
    if (error.response?.status === 404) {
      const customError = new Error('P2P endpoints not implemented. Please add P2P routes to your backend.');
      customError.name = 'P2P_NOT_IMPLEMENTED';
      throw customError;
    }
    
    throw error;
  }
};

// Mock Data for Development (when backend is not available)
const MOCK_OFFERS: Offer[] = [
  {
    id: '1',
    userId: 'user1',
    userEmail: 'trader1@example.com',
    username: 'CryptoTrader1',
    cryptoType: 'BTC',
    paymentMethod: 'Bank Transfer',
    price: 45000,
    amount: 0.5,
    minAmount: 0.01,
    maxAmount: 1.0,
    terms: 'Payment within 15 minutes',
    autoReply: 'Please send payment proof',
    completionRate: 98,
    totalTrades: 156,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    status: 'active',
    type: 'buy',
  },
  {
    id: '2',
    userId: 'user2',
    userEmail: 'trader2@example.com',
    username: 'EthereumSeller',
    cryptoType: 'ETH',
    paymentMethod: 'PayPal',
    price: 3200,
    amount: 2.5,
    minAmount: 0.1,
    maxAmount: 5.0,
    terms: 'Instant payment required',
    autoReply: 'Crypto will be sent after payment confirmation',
    completionRate: 95,
    totalTrades: 89,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    status: 'active',
    type: 'sell',
  },
  {
    id: '3',
    userId: 'user3',
    userEmail: 'trader3@example.com',
    username: 'StableCoinBuyer',
    cryptoType: 'USDT',
    paymentMethod: 'Wise',
    price: 1.00,
    amount: 1000,
    minAmount: 100,
    maxAmount: 10000,
    terms: 'Bank transfer only',
    autoReply: 'Please provide transaction ID',
    completionRate: 100,
    totalTrades: 234,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    status: 'active',
    type: 'buy',
  },
  {
    id: '4',
    userId: 'user4',
    userEmail: 'trader4@example.com',
    username: 'BNBTrader',
    cryptoType: 'BNB',
    paymentMethod: 'Cash App',
    price: 350,
    amount: 50,
    minAmount: 1,
    maxAmount: 100,
    terms: 'Cash App only, instant transfer',
    autoReply: 'Send screenshot of payment',
    completionRate: 92,
    totalTrades: 67,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    status: 'active',
    type: 'sell',
  },
  {
    id: '5',
    userId: 'user5',
    userEmail: 'trader5@example.com',
    username: 'USDCBuyer',
    cryptoType: 'USDC',
    paymentMethod: 'Zelle',
    price: 1.00,
    amount: 5000,
    minAmount: 50,
    maxAmount: 50000,
    terms: 'Zelle transfer only',
    autoReply: 'Please provide Zelle confirmation',
    completionRate: 99,
    totalTrades: 312,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    status: 'active',
    type: 'buy',
  },
  {
    id: '6',
    userId: 'user6',
    userEmail: 'trader6@example.com',
    username: 'BitcoinSeller',
    cryptoType: 'BTC',
    paymentMethod: 'Venmo',
    price: 46000,
    amount: 0.25,
    minAmount: 0.01,
    maxAmount: 0.5,
    terms: 'Venmo payment required',
    autoReply: 'Crypto sent after payment verification',
    completionRate: 94,
    totalTrades: 123,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    status: 'active',
    type: 'sell',
  },
];

const MOCK_TRADES: Trade[] = [
  {
    id: 'trade-1',
    offerId: '1',
    buyerId: 'buyer1',
    sellerId: 'seller1',
    buyerEmail: 'buyer@example.com',
    sellerEmail: 'seller@example.com',
    cryptoType: 'BTC',
    paymentMethod: 'Bank Transfer',
    amount: 0.1,
    price: 45000,
    totalAmount: 4500,
    status: 'pending',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'trade-2',
    offerId: '2',
    buyerId: 'buyer2',
    sellerId: 'seller2',
    buyerEmail: 'buyer2@example.com',
    sellerEmail: 'seller2@example.com',
    cryptoType: 'ETH',
    paymentMethod: 'PayPal',
    amount: 0.5,
    price: 3200,
    totalAmount: 1600,
    status: 'completed',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
];

const MOCK_USER_PROFILE: UserProfile = {
  email: 'user@example.com',
  username: 'CryptoTrader',
  completionRate: 96,
  totalTrades: 45,
  totalVolume: 125000,
  positiveReviews: 42,
  negativeReviews: 2,
  averageRating: 4.8,
  joinDate: '2024-01-15T00:00:00.000Z',
  lastActive: new Date().toISOString(),
  isVerified: true,
  activeOffers: 3,
};

// Helper function to check if backend is available
const isBackendAvailable = async (): Promise<boolean> => {
  try {
    // Check if buy offers endpoint is available
    console.log('🔍 Checking backend availability at:', P2P_BASE_URL + '/buy');
    const testResponse = await P2P_API.get('/buy');
    console.log('✅ Backend is available - using real data');
    console.log('📡 Backend response sample:', testResponse.data);
    return true;
  } catch (error: any) {
    console.log('❌ Backend not available');
    console.log('   Error status:', error.response?.status);
    console.log('   Error message:', error.message);
    console.log('   Error code:', error.code);
    console.log('   Full error:', error);
    // If offers endpoint returns 404, backend doesn't have P2P routes
    // If connection is refused, backend might not be running
    if (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK') {
      console.log('⚠️  Connection refused - is your backend running on localhost:5000?');
    }
    return error.response?.status !== 404;
  }
};

// Types for P2P Trading
export interface Offer {
  id: string;
  userId: string;
  userEmail: string;
  username: string;
  cryptoType: string;
  paymentMethod: string;
  price: number;
  amount: number;
  minAmount: number;
  maxAmount: number;
  terms: string;
  autoReply: string;
  completionRate: number;
  totalTrades: number;
  createdAt: string;
  updatedAt: string;
  status: 'active' | 'paused' | 'deleted';
  type: 'buy' | 'sell';
  // Additional fields for "in return" tokens
  returnToken?: string;
  returnAmount?: number;
  fiatCurrency?: string;
  // Additional field for all payment methods
  paymentMethods?: string[];
  // Payment method type: 'REAL_FIAT' for gateway payments, 'INTERNAL_USD' for asset wallet
  paymentMethodType?: 'REAL_FIAT' | 'INTERNAL_USD';
}

export interface OffersPagination {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface OffersResponse {
  data: Offer[];
  pagination: OffersPagination;
}

export interface Trade {
  id: string;
  offerId: string;
  buyerId: string;
  sellerId: string;
  buyerEmail: string;
  sellerEmail: string;
  cryptoType: string;
  paymentMethod: string;
  amount: number;
  price: number;
  totalAmount: number;
  status: 'pending' | 'paid' | 'crypto_sent' | 'completed' | 'cancelled' | 'disputed';
  createdAt: string;
  updatedAt: string;
  paymentDetails?: string;
  cryptoTransactionHash?: string;
  disputeReason?: string;
  disputeStatus?: 'open' | 'resolved' | 'closed';
}

export interface UserProfile {
  email: string;
  username: string;
  completionRate: number;
  totalTrades: number;
  totalVolume?: number;
  positiveReviews: number;
  negativeReviews: number;
  averageRating: number;
  joinDate: string;
  lastActive: string;
  isVerified: boolean;
  activeOffers?: number;
}

export interface Rating {
  id: string;
  fromUser: string;
  toUser: string;
  rating: number;
  comment: string;
  tradeId: string;
  createdAt: string;
}

export interface Dispute {
  id: string;
  tradeId: string;
  complainantId: string;
  respondentId: string;
  reason: string;
  description: string;
  status: 'open' | 'under_review' | 'resolved' | 'closed';
  evidence: string[];
  resolution?: string;
  createdAt: string;
  updatedAt: string;
}

export interface Wallet {
  id: string;
  userId: string;
  cryptoType: string;
  balance: number;
  frozenBalance: number;
  address?: string;
}

// Offer Management APIs
export const getOffers = async (filters?: {
  cryptoType?: string;
  paymentMethod?: string;
  type?: '' | 'buy' | 'sell';
  minPrice?: number;
  maxPrice?: number;
  sortBy?: 'price' | 'volume' | 'completionRate';
  sortOrder?: 'asc' | 'desc';
  page?: number;
  limit?: number;
}): Promise<OffersResponse> => {
  try {
    // Check if backend is available
    const backendAvailable = await isBackendAvailable();
    console.log('Backend available:', backendAvailable);
    
    if (!backendAvailable) {
      console.log('Using mock data');
      // Return mock data with filtering
      let filteredOffers = [...MOCK_OFFERS];
      
      if (filters) {
        if (filters.type) {
          filteredOffers = filteredOffers.filter(offer => offer.type === filters.type);
        }
        if (filters.cryptoType) {
          filteredOffers = filteredOffers.filter(offer => offer.cryptoType === filters.cryptoType);
        }
        if (filters.paymentMethod) {
          filteredOffers = filteredOffers.filter(offer => offer.paymentMethod === filters.paymentMethod);
        }
        if (filters.minPrice) {
          filteredOffers = filteredOffers.filter(offer => offer.price >= filters.minPrice!);
        }
        if (filters.maxPrice) {
          filteredOffers = filteredOffers.filter(offer => offer.price <= filters.maxPrice!);
        }
      }
      
      return {
        data: filteredOffers,
        pagination: {
          total: filteredOffers.length,
          page: filters?.page || 1,
          limit: filters?.limit || 10,
          totalPages: Math.ceil(filteredOffers.length / (filters?.limit || 10))
        }
      };
    }
    
    console.log('Using real backend data with specific endpoints');
    
    let response;
    
    if (filters?.type === 'buy') {
      console.log('Using /buy endpoint');
      const params = new URLSearchParams();
      if (filters) {
        Object.entries(filters).forEach(([key, value]) => {
          // Include all filter params including page and limit
          if (key !== 'type' && value !== undefined && value !== null && value !== '') {
            params.append(key, value.toString());
          }
        });
      }
      const url = params.toString() ? `/buy?${params.toString()}` : '/buy';
      console.log('API URL:', url);
      console.log('Limit being used:', filters?.limit || 'not set');
      response = await P2P_API.get(url);
    } else if (filters?.type === 'sell') {
      console.log('Using /sell endpoint');
      const params = new URLSearchParams();
      if (filters) {
        Object.entries(filters).forEach(([key, value]) => {
          // Include all filter params including page and limit
          if (key !== 'type' && value !== undefined && value !== null && value !== '') {
            params.append(key, value.toString());
          }
        });
      }
      const url = params.toString() ? `/sell?${params.toString()}` : '/sell';
      console.log('API URL:', url);
      console.log('Limit being used:', filters?.limit || 'not set');
      response = await P2P_API.get(url);
    } else {
      console.log('Using both /buy and /sell endpoints for all offers');
      // Fetch both buy and sell offers and combine them
      const params = new URLSearchParams();
      if (filters) {
        Object.entries(filters).forEach(([key, value]) => {
          // Include all filter params including page and limit
          if (key !== 'type' && value !== undefined && value !== null && value !== '') {
            params.append(key, value.toString());
          }
        });
      }
      
      // Ensure limit is set for fetching all offers (if not already set)
      if (!filters?.limit && filters?.page === undefined) {
        params.append('limit', '1000'); // High limit to get all offers
      }
      
      const buyUrl = params.toString() ? `/buy?${params.toString()}` : '/buy';
      const sellUrl = params.toString() ? `/sell?${params.toString()}` : '/sell';
      
      console.log('Buy API URL:', buyUrl);
      console.log('Sell API URL:', sellUrl);
      console.log('Limit being used:', filters?.limit || '1000 (default for all offers)');
      
      const [buyResponse, sellResponse] = await Promise.all([
        P2P_API.get(buyUrl),
        P2P_API.get(sellUrl)
      ]);
      
      // Combine the responses - handle different response structures
      const getOffersArray = (responseData: any): any[] => {
        if (Array.isArray(responseData)) return responseData;
        if (Array.isArray(responseData?.data)) return responseData.data;
        if (Array.isArray(responseData?.offers)) return responseData.offers;
        return [];
      };
      
      const buyOffers = getOffersArray(buyResponse.data);
      const sellOffers = getOffersArray(sellResponse.data);
      const combinedOffers = [...buyOffers, ...sellOffers];
      
      response = {
        data: {
          data: combinedOffers
        }
      };
      
      console.log('Combined offers - Buy:', buyOffers.length, 'Sell:', sellOffers.length, 'Total:', combinedOffers.length);
    }
    
    // Map backend data to frontend format
    console.log('📦 Raw backend response:', response.data);
    console.log('📦 Response data type:', typeof response.data);
    console.log('📦 Is response.data an array?', Array.isArray(response.data));
    console.log('📦 Response.data.data:', response.data?.data);
    console.log('📦 Response.data.data type:', typeof response.data?.data);
    console.log('📦 Is response.data.data an array?', Array.isArray(response.data?.data));
    
    // Backend returns { status: 200, data: [...], message: "..." } or direct array
    // Try multiple possible response structures
    let offersArray: any[] = [];
    
    if (Array.isArray(response.data)) {
      // Backend returned array directly
      offersArray = response.data;
      console.log('✅ Response is direct array');
    } else if (Array.isArray(response.data?.data)) {
      // Backend returned { data: [...] }
      offersArray = response.data.data;
      console.log('✅ Response has data.data array');
    } else if (Array.isArray(response.data?.offers)) {
      // Backend returned { offers: [...] }
      offersArray = response.data.offers;
      console.log('✅ Response has data.offers array');
    } else if (response.data?.data && Array.isArray(response.data.data)) {
      offersArray = response.data.data;
      console.log('✅ Response has nested data.data array');
    } else {
      console.error('❌ Invalid backend response structure');
      console.error('   Expected array or { data: [...] }');
      console.error('   Got:', JSON.stringify(response.data, null, 2));
      return {
        data: [],
        pagination: { total: 0, page: 1, limit: 10, totalPages: 0 }
      };
    }
    
    console.log('📦 Offers array:', offersArray.length, 'items');
    
    const mappedOffers: Offer[] = offersArray.map((offer: any) => {
      console.log('📝 Mapping offer:', {
        offerId: offer.offerId,
        offerType: offer.offerType,
        type: offer.type,
        cryptoCurrency: offer.cryptoCurrency,
        baseToken: offer.baseToken,
        quoteToken: offer.quoteToken,
        creatorEmail: offer.creatorEmail,
        paymentMethod: offer.paymentMethod,
        paymentMethods: offer.paymentMethods,
        settlement: offer.settlement,
        paymentMethodType: offer.paymentMethodType,
        paymentSettlement: offer.paymentSettlement,
        settlementType: offer.settlementType
      });
      
      // Handle payment methods - Priority: acceptedPaymentMethods > paymentMethods array > paymentMethod string
      // NOTE: settlement field (REAL_FIAT/INTERNAL_USD) is NOT a payment method, it's a settlement type
      let paymentMethodsArray: string[] = [];
      
      // First check for acceptedPaymentMethods (from create offer API)
      if (offer.acceptedPaymentMethods && Array.isArray(offer.acceptedPaymentMethods) && offer.acceptedPaymentMethods.length > 0) {
        paymentMethodsArray = offer.acceptedPaymentMethods;
      }
      
      // Second check for paymentMethods array (from backend response)
      if (paymentMethodsArray.length === 0 && offer.paymentMethods && Array.isArray(offer.paymentMethods) && offer.paymentMethods.length > 0) {
        paymentMethodsArray = offer.paymentMethods;
      }
      
      // Third check settlement field - but only if it's NOT a settlement type (REAL_FIAT/INTERNAL_USD)
      // Settlement can be an array of payment methods in some old formats
      if (paymentMethodsArray.length === 0 && offer.settlement) {
        const settlement = offer.settlement;
        const settlementUpper = typeof settlement === 'string' ? settlement.toUpperCase().trim() : '';
        
        // Only treat settlement as payment methods if it's NOT a known settlement type
        if (settlementUpper !== 'REAL_FIAT' && 
            settlementUpper !== 'REAL FIAT' && 
            settlementUpper !== 'INTERNAL_USD' && 
            settlementUpper !== 'INTERNALUSD' &&
            settlementUpper !== 'ASSET_WALLET' &&
            settlementUpper !== 'ASSET WALLET') {
          if (Array.isArray(settlement)) {
            paymentMethodsArray = settlement;
          } else if (typeof settlement === 'string') {
            paymentMethodsArray = settlement.includes(',') 
              ? settlement.split(',').map((m: string) => m.trim()).filter((m: string) => m)
              : [settlement.trim()];
          }
        }
      }
      
      // Last check paymentMethod string
      if (paymentMethodsArray.length === 0 && offer.paymentMethod) {
        if (typeof offer.paymentMethod === 'string') {
          const methodUpper = offer.paymentMethod.toUpperCase().trim();
          // Don't use paymentMethod if it's a settlement type
          if (methodUpper !== 'REAL_FIAT' && 
              methodUpper !== 'REAL FIAT' && 
              methodUpper !== 'INTERNAL_USD' && 
              methodUpper !== 'INTERNALUSD' &&
              methodUpper !== 'ASSET_WALLET' &&
              methodUpper !== 'ASSET WALLET') {
            paymentMethodsArray = offer.paymentMethod.includes(',') 
              ? offer.paymentMethod.split(',').map((m: string) => m.trim()).filter((m: string) => m)
              : [offer.paymentMethod.trim()];
          }
        }
      }
      
      // If still no payment methods, check settlement type and set appropriate payment method
      if (paymentMethodsArray.length === 0 && offer.settlement) {
        const settlementUpper = typeof offer.settlement === 'string' ? offer.settlement.toUpperCase().trim() : '';
        
        // If settlement is INTERNAL_USD or ASSET_WALLET, set payment method to "Asset Wallet"
        if (settlementUpper === 'INTERNAL_USD' || 
            settlementUpper === 'INTERNALUSD' ||
            settlementUpper === 'ASSET_WALLET' ||
            settlementUpper === 'ASSET WALLET') {
          paymentMethodsArray = ['Asset Wallet'];
        }
        // If settlement is REAL_FIAT but no payment methods specified, we'll leave it empty
        // (This should rarely happen as REAL_FIAT offers should have acceptedPaymentMethods)
      }
      
      // Get the primary payment method for display
      const primaryPaymentMethod = paymentMethodsArray.length > 0 
        ? paymentMethodsArray[0] 
        : (offer.paymentMethod && offer.paymentMethod.toUpperCase().trim() !== 'REAL_FIAT' && 
            offer.paymentMethod.toUpperCase().trim() !== 'INTERNAL_USD' ? offer.paymentMethod : 'Bank Transfer');
      
      console.log('💳 Payment method mapping:', {
        settlement: offer.settlement,
        originalPaymentMethod: offer.paymentMethod,
        originalPaymentMethods: offer.paymentMethods,
        paymentMethodsArray: paymentMethodsArray,
        primaryPaymentMethod: primaryPaymentMethod
      });
      
      // Handle crypto currency - check both old and new field names
      const cryptoType = offer.cryptoCurrency || offer.quoteToken || offer.cryptoType || offer.baseToken;
      
      // Handle offer type - normalize to lowercase
      const offerType = offer.offerType || offer.type || 'buy';
      const normalizedType: 'buy' | 'sell' = typeof offerType === 'string'
        ? offerType.toLowerCase().trim() === 'sell'
          ? 'sell'
          : 'buy'
        : 'buy';
      
      return {
        id: offer.offerId || offer._id || offer.id,
        userId: offer._id || offer.userId,
        userEmail: offer.creatorEmail || offer.userEmail,
        username: offer.creatorEmail ? offer.creatorEmail.split('@')[0] : 'Trader',
        cryptoType: cryptoType,
        paymentMethod: paymentMethodsArray.length > 0 ? paymentMethodsArray.join(', ') : primaryPaymentMethod,
        price: offer.pricePerUnit || offer.price,
        amount: offer.availableAmount || offer.amount,
        minAmount: offer.minAmount || 0,
        maxAmount: offer.maxAmount || 0,
        terms: offer.terms || 'No terms specified',
        autoReply: offer.autoReply || '',
        completionRate: offer.completionRate || 100,
        totalTrades: offer.totalTrades || 0,
        createdAt: offer.createdAt || new Date().toISOString(),
        updatedAt: offer.updatedAt || new Date().toISOString(),
        status: offer.status ? offer.status.toLowerCase() : 'active',
        type: normalizedType,
        // Map return token fields - fiatCurrency is what admin will give/return to user
        returnToken: offer.fiatCurrency || offer.returnToken || offer.tokenInReturn || undefined,
        returnAmount: offer.returnAmount || undefined,
        fiatCurrency: offer.fiatCurrency || 'USD',
        paymentMethods: paymentMethodsArray,
        // Map paymentMethodType - check settlement field first, then other fields
        // Check if paymentMethods array contains InternalUSD or ASSET_WALLET related methods
        paymentMethodType: offer.paymentMethodType || offer.paymentSettlement || offer.settlementType || 
          (() => {
            // Use the processed paymentMethodsArray to check for Asset Wallet methods
            const allMethods = paymentMethodsArray.map(m => (m || '').toUpperCase().trim());
            
            // Also check the settlement field directly
            const settlementValue = offer.settlement ? (typeof offer.settlement === 'string' ? offer.settlement.toUpperCase().trim() : '') : '';
            
            const hasInternalUsd = allMethods.some(m => 
              m === 'ASSET WALLET' ||
              m === 'ASSET_WALLET' ||
              m === 'INTERNALUSD' || 
              m === 'INTERNAL_USD' || 
              m === 'INTERNAL' ||
              m.includes('INTERNALUSD') || 
              m.includes('INTERNAL_USD') ||
              m.includes('ASSET')
            ) || (
              settlementValue === 'ASSET WALLET' ||
              settlementValue === 'ASSET_WALLET' ||
              settlementValue.includes('ASSET') ||
              settlementValue.includes('INTERNAL')
            );
            
            return hasInternalUsd ? 'INTERNAL_USD' : 'REAL_FIAT';
          })(),
      };
    });
    
    console.log('Mapped offers count:', mappedOffers.length);
    console.log('Mapped offer types:', mappedOffers.map(o => ({ id: o.id, type: o.type, crypto: o.cryptoType })));
    
    // Apply additional client-side filtering for other filters (type is handled by endpoint)
    let filteredOffers = mappedOffers;
    
    if (filters) {
      if (filters.cryptoType && filters.cryptoType !== '') {
        filteredOffers = filteredOffers.filter(offer => offer.cryptoType === filters.cryptoType);
      }
      if (filters.paymentMethod && filters.paymentMethod !== '') {
        filteredOffers = filteredOffers.filter(offer => offer.paymentMethod === filters.paymentMethod);
      }
      if (filters.minPrice) {
        filteredOffers = filteredOffers.filter(offer => offer.price >= filters.minPrice!);
      }
      if (filters.maxPrice) {
        filteredOffers = filteredOffers.filter(offer => offer.price <= filters.maxPrice!);
      }
    }
    
    console.log('Final filtered offers count:', filteredOffers.length);
    
    return {
      data: filteredOffers,
      pagination: {
        total: filteredOffers.length,
        page: filters?.page || 1,
        limit: filters?.limit || 10,
        totalPages: Math.ceil(filteredOffers.length / (filters?.limit || 10))
      }
    };
  } catch (error: any) {
    console.error('Failed to fetch offers:', error);
    throw error.response?.data || error;
  }
};

export const createOffer = async (offerData: {
  cryptoType: string;
  paymentMethod: string;
  price: number;
  amount: number;
  minAmount: number;
  maxAmount: number;
  terms: string;
  autoReply: string;
  type: 'buy' | 'sell';
}) => {
  try {
    const response = await P2P_API.post('/offers', offerData);
    return response.data;
  } catch (error: any) {
    console.error('Failed to create offer:', error);
    throw error.response?.data || error;
  }
};

export const getOfferById = async (offerId: string) => {
  try {
    console.log('Fetching offer by ID:', offerId);
    const response = await P2P_API.get(`/offers/${offerId}`);
    console.log('Offer API response:', response.data);
    
    // Map backend data to frontend format - handle different response structures
    let backendOffer = response.data.data || response.data;
    
    // If it's not an object, try to get it from other possible locations
    if (!backendOffer || typeof backendOffer !== 'object') {
      backendOffer = response.data.offer || response.data;
    }
    
    console.log('Raw backend offer data:', {
      settlement: backendOffer.settlement,
      paymentMethodType: backendOffer.paymentMethodType,
      paymentSettlement: backendOffer.paymentSettlement,
      settlementType: backendOffer.settlementType,
      paymentMethod: backendOffer.paymentMethod,
      paymentMethods: backendOffer.paymentMethods,
      fullOffer: backendOffer
    });
    
    // Handle payment methods - same logic as in getOffers
    // Priority: acceptedPaymentMethods > paymentMethods array > paymentMethod string
    // NOTE: settlement field (REAL_FIAT/INTERNAL_USD) is NOT a payment method, it's a settlement type
    let paymentMethodsArray: string[] = [];
    
    // First check for acceptedPaymentMethods (from create offer API)
    if (backendOffer.acceptedPaymentMethods && Array.isArray(backendOffer.acceptedPaymentMethods) && backendOffer.acceptedPaymentMethods.length > 0) {
      paymentMethodsArray = backendOffer.acceptedPaymentMethods;
    }
    
    // Second check for paymentMethods array (from backend response)
    if (paymentMethodsArray.length === 0 && backendOffer.paymentMethods && Array.isArray(backendOffer.paymentMethods) && backendOffer.paymentMethods.length > 0) {
      paymentMethodsArray = backendOffer.paymentMethods;
    }
    
    // Third check settlement field - but only if it's NOT a settlement type (REAL_FIAT/INTERNAL_USD)
    // Settlement can be an array of payment methods in some old formats
    if (paymentMethodsArray.length === 0 && backendOffer.settlement) {
      const settlement = backendOffer.settlement;
      const settlementUpper = typeof settlement === 'string' ? settlement.toUpperCase().trim() : '';
      
      // Only treat settlement as payment methods if it's NOT a known settlement type
      if (settlementUpper !== 'REAL_FIAT' && 
          settlementUpper !== 'REAL FIAT' && 
          settlementUpper !== 'INTERNAL_USD' && 
          settlementUpper !== 'INTERNALUSD' &&
          settlementUpper !== 'ASSET_WALLET' &&
          settlementUpper !== 'ASSET WALLET') {
        if (Array.isArray(settlement)) {
          paymentMethodsArray = settlement;
        } else if (typeof settlement === 'string') {
          paymentMethodsArray = settlement.includes(',') 
            ? settlement.split(',').map((m: string) => m.trim()).filter((m: string) => m)
            : [settlement.trim()];
        }
      }
    }
    
    // Last check paymentMethod string
    if (paymentMethodsArray.length === 0 && backendOffer.paymentMethod) {
      if (typeof backendOffer.paymentMethod === 'string') {
        const methodUpper = backendOffer.paymentMethod.toUpperCase().trim();
        // Don't use paymentMethod if it's a settlement type
        if (methodUpper !== 'REAL_FIAT' && 
            methodUpper !== 'REAL FIAT' && 
            methodUpper !== 'INTERNAL_USD' && 
            methodUpper !== 'INTERNALUSD' &&
            methodUpper !== 'ASSET_WALLET' &&
            methodUpper !== 'ASSET WALLET') {
          paymentMethodsArray = backendOffer.paymentMethod.includes(',') 
            ? backendOffer.paymentMethod.split(',').map((m: string) => m.trim()).filter((m: string) => m)
            : [backendOffer.paymentMethod.trim()];
        }
      }
    }
    
    // If still no payment methods, check settlement type and set appropriate payment method
    if (paymentMethodsArray.length === 0 && backendOffer.settlement) {
      const settlementUpper = typeof backendOffer.settlement === 'string' ? backendOffer.settlement.toUpperCase().trim() : '';
      
      // If settlement is INTERNAL_USD or ASSET_WALLET, set payment method to "Asset Wallet"
      if (settlementUpper === 'INTERNAL_USD' || 
          settlementUpper === 'INTERNALUSD' ||
          settlementUpper === 'ASSET_WALLET' ||
          settlementUpper === 'ASSET WALLET') {
        paymentMethodsArray = ['Asset Wallet'];
      }
      // If settlement is REAL_FIAT but no payment methods specified, we'll leave it empty
      // (This should rarely happen as REAL_FIAT offers should have acceptedPaymentMethods)
    }
    
    const primaryPaymentMethod = paymentMethodsArray.length > 0 
      ? paymentMethodsArray[0] 
      : (backendOffer.paymentMethod && backendOffer.paymentMethod.toUpperCase().trim() !== 'REAL_FIAT' && 
          backendOffer.paymentMethod.toUpperCase().trim() !== 'INTERNAL_USD' ? backendOffer.paymentMethod : 'Bank Transfer');
    
    // Handle crypto currency - check both old and new field names
    const cryptoType = backendOffer.cryptoCurrency || backendOffer.quoteToken || backendOffer.cryptoType || backendOffer.baseToken;
    
    // Handle offer type - normalize to lowercase
    const offerType = backendOffer.offerType || backendOffer.type || 'buy';
    const normalizedType = typeof offerType === 'string' ? offerType.toLowerCase() : 'buy';
    
    const mappedOffer = {
      id: backendOffer.offerId || backendOffer._id || backendOffer.id,
      userId: backendOffer._id || backendOffer.userId,
      userEmail: backendOffer.creatorEmail || backendOffer.userEmail,
      username: backendOffer.creatorEmail ? backendOffer.creatorEmail.split('@')[0] : 'Trader',
      cryptoType: cryptoType,
      paymentMethod: paymentMethodsArray.length > 0 ? paymentMethodsArray.join(', ') : primaryPaymentMethod,
      paymentMethods: paymentMethodsArray,
      price: backendOffer.pricePerUnit || backendOffer.price,
      amount: backendOffer.availableAmount || backendOffer.amount,
      minAmount: backendOffer.minAmount || 0,
      maxAmount: backendOffer.maxAmount || 0,
      terms: backendOffer.terms || 'No terms specified',
      autoReply: backendOffer.autoReply || '',
      completionRate: backendOffer.completionRate || 100,
      totalTrades: backendOffer.totalTrades || 0,
      createdAt: backendOffer.createdAt || new Date().toISOString(),
      updatedAt: backendOffer.updatedAt || new Date().toISOString(),
      status: backendOffer.status ? backendOffer.status.toLowerCase() : 'active',
      type: normalizedType,
      // Map return token - fiatCurrency is what admin will give/return to user
      returnToken: backendOffer.fiatCurrency || backendOffer.returnToken || backendOffer.tokenInReturn || undefined,
      returnAmount: backendOffer.returnAmount || undefined,
      fiatCurrency: backendOffer.fiatCurrency || 'USD',
      // Map paymentMethodType - check settlement field first, then other fields
      // Check if paymentMethods array contains InternalUSD or ASSET_WALLET related methods
      paymentMethodType: backendOffer.paymentMethodType || backendOffer.paymentSettlement || backendOffer.settlementType || 
        (() => {
          // Use the processed paymentMethodsArray to check for Asset Wallet methods
          const allMethods = paymentMethodsArray.map(m => (m || '').toUpperCase().trim());
          
          // Also check the settlement field directly
          const settlementValue = backendOffer.settlement ? (typeof backendOffer.settlement === 'string' ? backendOffer.settlement.toUpperCase().trim() : '') : '';
          
          const hasInternalUsd = allMethods.some(m => 
            m === 'ASSET WALLET' ||
            m === 'ASSET_WALLET' ||
            m === 'INTERNALUSD' || 
            m === 'INTERNAL_USD' || 
            m === 'INTERNAL' ||
            m.includes('INTERNALUSD') || 
            m.includes('INTERNAL_USD') ||
            m.includes('ASSET')
          ) || (
            settlementValue === 'ASSET WALLET' ||
            settlementValue === 'ASSET_WALLET' ||
            settlementValue.includes('ASSET') ||
            settlementValue.includes('INTERNAL')
          );
          
          return hasInternalUsd ? 'INTERNAL_USD' : 'REAL_FIAT';
        })(),
    };
    
    console.log('Mapped offer with paymentMethodType:', {
      ...mappedOffer,
      paymentMethodType: mappedOffer.paymentMethodType
    });
    
    return {
      success: true,
      data: mappedOffer,
      message: response.data.message
    };
  } catch (error: any) {
    console.error('Failed to fetch offer:', error);
    throw error.response?.data || error;
  }
};

export const updateOffer = async (offerId: string, updateData: Partial<Offer>) => {
  try {
    const response = await P2P_API.put(`/offers/${offerId}`, updateData);
    return response.data;
  } catch (error: any) {
    console.error('Failed to update offer:', error);
    throw error.response?.data || error;
  }
};

export const deleteOffer = async (offerId: string) => {
  try {
    const response = await P2P_API.delete(`/offers/${offerId}`);
    return response.data;
  } catch (error: any) {
    console.error('Failed to delete offer:', error);
    throw error.response?.data || error;
  }
};

// Trade Management APIs
// Join Offer API - New endpoint for joining offers
export const joinOffer = async (offerId: string, amount: number) => {
  try {
    console.log('Joining offer:', offerId, 'with amount:', amount);
    const response = await P2P_API.post(`/offers/${offerId}/join`, {
      amount,
    });
    return {
      success: true,
      data: response.data.data || response.data,
      tradeId: response.data.data?.tradeId || response.data.data?.id,
      requiredAction: response.data.data?.requiredAction || 'NONE',
      paymentIntent: response.data.data?.paymentIntent,
    };
  } catch (error: any) {
    console.error('Failed to join offer:', error);
    throw error.response?.data || error;
  }
};

// Join Offer with Gateway Payment (REAL_FIAT)
export const joinOfferWithGateway = async (offerId: string, amount: number, paymentMethod: string) => {
  try {
    console.log('Joining offer with gateway payment:', offerId, 'amount:', amount, 'method:', paymentMethod);
    const response = await P2P_API.post(`/offers/${offerId}/join-gateway`, {
      amount,
      paymentMethod,
    });
    return {
      success: true,
      data: response.data.data || response.data,
      tradeId: response.data.data?.tradeId || response.data.data?.id,
      requiredAction: response.data.data?.requiredAction || 'NONE',
      paymentIntent: response.data.data?.paymentIntent,
    };
  } catch (error: any) {
    console.error('Failed to join offer with gateway:', error);
    throw error.response?.data || error;
  }
};

// Join Offer with Asset Wallet (INTERNAL_USD)
export const joinOfferWithAsset = async (offerId: string, amount: number) => {
  try {
    // Get logged-in user's email from localStorage
    const userEmail = localStorage.getItem('user') || localStorage.getItem('email');
    
    if (!userEmail) {
      throw new Error('User email not found. Please log in again.');
    }
    
    console.log('Joining offer with asset wallet:', offerId, 'amount:', amount, 'email:', userEmail);
    const response = await P2P_API.post(`/offers/${offerId}/join-asset`, {
      amount,
      email: userEmail,
    });
    return {
      success: true,
      data: response.data.data || response.data,
      tradeId: response.data.data?.tradeId || response.data.data?.id,
      requiredAction: response.data.data?.requiredAction || 'NONE',
    };
  } catch (error: any) {
    console.error('Failed to join offer with asset wallet:', error);
    throw error.response?.data || error;
  }
};

// Confirm DB USD Payment
export const confirmDbUsdPayment = async (tradeId: string) => {
  try {
    console.log('Confirming DB USD payment for trade:', tradeId);
    const response = await P2P_API.post(`/trades/${tradeId}/confirm-db-usd`);
    return response.data;
  } catch (error: any) {
    console.error('Failed to confirm DB USD payment:', error);
    throw error.response?.data || error;
  }
};

export const createTrade = async (tradeData: {
  offerId: string;
  amount: number;
  paymentDetails?: string;
}) => {
  try {
    console.log('Creating trade with auto-escrow and asset transfer:', tradeData);
    
    // Step 1: Get offer details first to extract payment method
    console.log('Getting offer details for payment method validation...');
    const offerResponse = await P2P_API.get(`/offers/${tradeData.offerId}`);
    const offer = offerResponse.data.data;
    
    console.log('Offer details:', offer);
    
    // Step 2: Create the trade with payment method information
    const cryptoAmount = tradeData.amount;
    const fiatAmount = cryptoAmount * offer.pricePerUnit;
    const escrowCryptoAmount = cryptoAmount; // Same as crypto amount for escrow
    
    const tradePayload = {
      offerId: tradeData.offerId,
      amount: tradeData.amount,
      cryptoAmount: cryptoAmount,
      fiatAmount: fiatAmount,
      escrowCryptoAmount: escrowCryptoAmount,
      paymentMethod: offer.paymentMethods?.[0] || 'BankTransfer', // Use first payment method from offer
      paymentDetails: tradeData.paymentDetails || `Payment via ${offer.paymentMethods?.[0] || 'BankTransfer'}`
    };
    
    console.log('Trade payload with payment method:', tradePayload);
    
    const tradeResponse = await P2P_API.post('/trades', tradePayload);
    console.log('Trade created:', tradeResponse.data);
    
    const tradeId = tradeResponse.data.data?.id || tradeResponse.data.data?._id;
    
    if (!tradeId) {
      throw new Error('Trade ID not found in response');
    }
    
    // Step 3: Get user info for asset transfer
    try {
      console.log('Getting user info for asset transfer...');
      
      const userEmail = localStorage.getItem('user');
      
      if (!userEmail) {
        throw new Error('User email not found');
      }
      
      console.log('User email:', userEmail);
      
      // Step 4: Determine escrow flow and transfer assets
      if (offer.offerType.toLowerCase() === 'sell') {
        // SELL offer - buyer joins, transfer crypto from buyer to escrow
        console.log('SELL offer detected - transferring crypto from buyer to escrow');
        
        try {
          // Transfer crypto from buyer to escrow
          const cryptoTransfer = await transferAssetsToEscrow(
            userEmail, // buyer email
            'escrow@indexx.exchange', // escrow wallet
            tradeData.amount,
            offer.cryptoCurrency,
            offer.cryptoNetwork || '',
            tradeId
          );
          console.log('Crypto transferred to escrow:', cryptoTransfer);
          
          // Send payment to escrow (simulated since escrow APIs don't exist)
          console.log('Simulating payment sent to escrow:', tradePayload.paymentDetails);
          
          // Auto-confirm trade completion for SELL offers
          console.log('Auto-confirming trade completion for SELL offer...');
          try {
            // Complete trade directly (bypass escrow APIs)
            console.log('Completing trade directly...');
            
            const directCompletion = await completeTradeDirectly(tradeId);
            console.log('Trade completed directly:', directCompletion);
            
            return {
              ...tradeResponse.data,
              escrowStatus: 'completed',
              assetTransfer: cryptoTransfer,
              directCompletion: directCompletion,
              message: 'Trade created and automatically completed! Assets transferred directly between users.'
            };
          } catch (autoConfirmError: any) {
            console.error('Failed to auto-confirm trade:', autoConfirmError);
            
            return {
              ...tradeResponse.data,
              escrowStatus: 'payment_sent',
              assetTransfer: cryptoTransfer,
              autoConfirmError: autoConfirmError.message,
              message: 'Trade created and payment sent to escrow. Manual confirmation required.'
            };
          }
        } catch (transferError: any) {
          console.error('Failed to transfer crypto to escrow:', transferError);
          // Still proceed with simulated payment escrow
          console.log('Simulating payment sent to escrow despite crypto transfer failure');
          
          return {
            ...tradeResponse.data,
            escrowStatus: 'payment_sent',
            assetTransferError: transferError.message,
            message: 'Trade created and payment sent to escrow, but crypto transfer failed'
          };
        }
      } else {
        // BUY offer - seller joins, transfer crypto from seller to escrow
        console.log('BUY offer detected - seller will transfer crypto to escrow');
        
        try {
          // Transfer crypto from seller to escrow
          const cryptoTransfer = await transferAssetsToEscrow(
            userEmail, // seller email
            'escrow@indexx.exchange', // escrow wallet
            tradeData.amount,
            offer.cryptoCurrency,
            offer.cryptoNetwork || '',
            tradeId
          );
          console.log('Crypto transferred to escrow by seller:', cryptoTransfer);
          
          // Auto-confirm trade completion for BUY offers
          console.log('Auto-confirming trade completion for BUY offer...');
          try {
            // Complete trade directly (bypass escrow APIs)
            console.log('Completing trade directly...');
            
            const directCompletion = await completeTradeDirectly(tradeId);
            console.log('Trade completed directly:', directCompletion);
            
            return {
              ...tradeResponse.data,
              escrowStatus: 'completed',
              assetTransfer: cryptoTransfer,
              directCompletion: directCompletion,
              message: 'Trade created and automatically completed! Assets transferred directly between users.'
            };
          } catch (autoConfirmError: any) {
            console.error('Failed to auto-confirm trade:', autoConfirmError);
            
            return {
              ...tradeResponse.data,
              escrowStatus: 'crypto_sent',
              assetTransfer: cryptoTransfer,
              autoConfirmError: autoConfirmError.message,
              message: 'Trade created and crypto transferred to escrow. Manual confirmation required.'
            };
          }
        } catch (transferError: any) {
          console.error('Failed to transfer crypto to escrow:', transferError);
          
          return {
            ...tradeResponse.data,
            escrowStatus: 'transfer_failed',
            assetTransferError: transferError.message,
            message: 'Trade created but crypto transfer to escrow failed'
          };
        }
      }
    } catch (escrowError: any) {
      console.error('Failed to process escrow:', escrowError);
      // Return trade creation success even if escrow fails
      return {
        ...tradeResponse.data,
        escrowStatus: 'escrow_failed',
        message: 'Trade created but escrow processing failed',
        escrowError: escrowError.message
      };
    }
  } catch (error: any) {
    console.error('Failed to create trade:', error);
    throw error.response?.data || error;
  }
};

export const getTrades = async (filters?: {
  status?: string;
  type?: 'buy' | 'sell';
  page?: number;
  limit?: number;
  email?: string;
}) => {
  try {
    // Check if backend is available
    const backendAvailable = await isBackendAvailable();
    console.log('Backend available:', backendAvailable);
    
    if (!backendAvailable) {
      console.log('Using mock data');
      // Return mock data with filtering
      let filteredTrades = [...MOCK_TRADES];
      
      if (filters?.status) {
        filteredTrades = filteredTrades.filter(trade => trade.status === filters.status);
      }
      
      return {
        data: filteredTrades,
        pagination: {
          total: filteredTrades.length,
          page: filters?.page || 1,
          limit: filters?.limit || 10,
          totalPages: Math.ceil(filteredTrades.length / (filters?.limit || 10))
        }
      };
    }
    
    console.log('Using real backend data');
    // Use real backend
    const params = new URLSearchParams();
    
    // Always include email parameter for backend
    const userEmail = filters?.email || localStorage.getItem('user');
    if (userEmail) {
      params.append('email', userEmail);
    }
    
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (key !== 'email' && value !== undefined && value !== null) {
          params.append(key, value.toString());
        }
      });
    }
    
    const response = await P2P_API.get(`/trades?${params.toString()}`);
    console.log('Raw trades response:', response.data);
    
    // Map backend response to frontend Trade interface
    const backendTrades = response.data.data || [];
    const mappedTrades: Trade[] = backendTrades.map((backendTrade: any) => ({
      id: backendTrade._id || backendTrade.tradeId,
      offerId: backendTrade.offerId,
      buyerId: backendTrade.buyerEmail, // Using email as ID for now
      sellerId: backendTrade.sellerEmail, // Using email as ID for now
      buyerEmail: backendTrade.buyerEmail,
      sellerEmail: backendTrade.sellerEmail,
      cryptoType: backendTrade.cryptoCurrency,
      paymentMethod: backendTrade.paymentMethod,
      amount: backendTrade.cryptoAmount,
      price: backendTrade.pricePerUnit,
      totalAmount: backendTrade.fiatAmount,
      status: backendTrade.status.toLowerCase(),
      createdAt: backendTrade.createdAt,
      updatedAt: backendTrade.updatedAt,
      paymentDetails: backendTrade.paymentDetails?.transactionId || backendTrade.paymentDetails,
      cryptoTransactionHash: backendTrade.cryptoTransactionHash,
      disputeReason: backendTrade.disputeReason,
      disputeStatus: backendTrade.disputeStatus,
    }));
    
    console.log('Mapped trades:', mappedTrades.length);
    
    return {
      data: mappedTrades,
      pagination: {
        total: mappedTrades.length,
        page: filters?.page || 1,
        limit: filters?.limit || 10,
        totalPages: Math.ceil(mappedTrades.length / (filters?.limit || 10))
      }
    };
  } catch (error: any) {
    console.error('Failed to fetch trades:', error);
    throw error.response?.data || error;
  }
};

export const getTradeById = async (tradeId: string) => {
  try {
    const response = await P2P_API.get(`/trades/${tradeId}`);
    console.log('Raw trade by ID response:', response.data);
    
    // Map backend response to frontend Trade interface (same as getTrades)
    const backendTrade = response.data.data || response.data;
    
    if (!backendTrade) {
      throw new Error('Trade not found');
    }
    
    const mappedTrade: Trade = {
      id: backendTrade._id || backendTrade.tradeId || backendTrade.id,
      offerId: backendTrade.offerId,
      buyerId: backendTrade.buyerEmail, // Using email as ID for now
      sellerId: backendTrade.sellerEmail, // Using email as ID for now
      buyerEmail: backendTrade.buyerEmail,
      sellerEmail: backendTrade.sellerEmail,
      cryptoType: backendTrade.cryptoCurrency || backendTrade.cryptoType,
      paymentMethod: backendTrade.paymentMethod,
      amount: backendTrade.cryptoAmount || backendTrade.amount,
      price: backendTrade.pricePerUnit || backendTrade.price,
      totalAmount: backendTrade.fiatAmount || backendTrade.totalAmount,
      status: backendTrade.status ? backendTrade.status.toLowerCase() : 'pending',
      createdAt: backendTrade.createdAt,
      updatedAt: backendTrade.updatedAt,
      paymentDetails: backendTrade.paymentDetails?.transactionId || backendTrade.paymentDetails,
      cryptoTransactionHash: backendTrade.cryptoTransactionHash,
      disputeReason: backendTrade.disputeReason,
      disputeStatus: backendTrade.disputeStatus,
    };
    
    console.log('Mapped trade:', mappedTrade);
    
    return {
      success: true,
      data: mappedTrade,
      message: response.data.message || 'Trade fetched successfully'
    };
  } catch (error: any) {
    console.error('Failed to fetch trade:', error);
    throw error.response?.data || error;
  }
};

export const cancelTrade = async (tradeId: string, reason?: string) => {
  try {
    console.log(`Attempting to cancel trade with ID: ${tradeId}`, reason ? `Reason: ${reason}` : '');
    const response = await P2P_API.post(`/trades/${tradeId}/cancel`, reason ? { reason } : {});
    console.log('Cancel trade API response:', response.data);
    return response.data;
  } catch (error: any) {
    console.error('Failed to cancel trade:', error);
    console.error('Error response:', {
      status: error?.response?.status,
      data: error?.response?.data,
      message: error?.message,
      tradeId: tradeId
    });
    
    // Re-throw with better error structure
    const errorData = error.response?.data || { message: error.message || 'Failed to cancel trade' };
    throw {
      ...errorData,
      status: error?.response?.status,
      tradeId: tradeId,
      originalError: error
    };
  }
};

// Get user pending trades
export const getUserPendingTrades = async (params?: { email?: string; limit?: number; offset?: number }) => {
  try {
    const queryParams = new URLSearchParams();
    
    // Get user email from localStorage if not provided
    const userEmail = params?.email || localStorage.getItem('user');
    if (userEmail) {
      queryParams.append('email', userEmail);
    }
    
    if (params?.limit) {
      queryParams.append('limit', params.limit.toString());
    }
    
    if (params?.offset !== undefined) {
      queryParams.append('offset', params.offset.toString());
    }
    
    const queryString = queryParams.toString();
    const url = `/trades/pending${queryString ? `?${queryString}` : ''}`;
    
    const response = await P2P_API.get(url);
    console.log('Raw pending trades response:', response.data);
    
    // Handle different response formats
    let backendTrades = [];
    if (Array.isArray(response.data)) {
      backendTrades = response.data;
    } else if (response.data?.data && Array.isArray(response.data.data)) {
      backendTrades = response.data.data;
    } else if (response.data?.trades && Array.isArray(response.data.trades)) {
      backendTrades = response.data.trades;
    } else {
      backendTrades = [];
    }
    
    // Map backend response to frontend Trade interface
    const mappedTrades: Trade[] = backendTrades.map((backendTrade: any) => ({
      id: backendTrade._id || backendTrade.tradeId || backendTrade.id,
      offerId: backendTrade.offerId,
      buyerId: backendTrade.buyerEmail || backendTrade.buyerId,
      sellerId: backendTrade.sellerEmail || backendTrade.sellerId,
      buyerEmail: backendTrade.buyerEmail,
      sellerEmail: backendTrade.sellerEmail,
      cryptoType: backendTrade.cryptoCurrency || backendTrade.cryptoType,
      paymentMethod: backendTrade.paymentMethod,
      amount: backendTrade.cryptoAmount || backendTrade.amount || 0,
      price: backendTrade.pricePerUnit || backendTrade.price || 0,
      totalAmount: backendTrade.fiatAmount || backendTrade.totalAmount || 0,
      status: backendTrade.status ? backendTrade.status.toLowerCase() : 'pending',
      createdAt: backendTrade.createdAt,
      updatedAt: backendTrade.updatedAt || backendTrade.createdAt,
      paymentDetails: backendTrade.paymentDetails?.transactionId || backendTrade.paymentDetails,
      cryptoTransactionHash: backendTrade.cryptoTransactionHash,
      disputeReason: backendTrade.disputeReason,
      disputeStatus: backendTrade.disputeStatus,
    }));
    
    console.log('Mapped pending trades:', mappedTrades.length);
    
    return {
      data: mappedTrades,
      pagination: {
        total: mappedTrades.length,
        page: 1,
        limit: mappedTrades.length,
        totalPages: 1,
      },
    };
  } catch (error: any) {
    console.error('Failed to fetch pending trades:', error);
    // Return empty array instead of throwing to prevent UI errors
    return {
      data: [],
      pagination: {
        total: 0,
        page: 1,
        limit: 0,
        totalPages: 0,
      },
    };
  }
};

// Get user completed trades
export const getUserCompletedTrades = async (params?: { email?: string; limit?: number; offset?: number }) => {
  try {
    const queryParams = new URLSearchParams();
    
    // Get user email from localStorage if not provided
    const userEmail = params?.email || localStorage.getItem('user');
    if (userEmail) {
      queryParams.append('email', userEmail);
    }
    
    if (params?.limit) {
      queryParams.append('limit', params.limit.toString());
    }
    
    if (params?.offset !== undefined) {
      queryParams.append('offset', params.offset.toString());
    }
    
    const queryString = queryParams.toString();
    const url = `/trades/completed${queryString ? `?${queryString}` : ''}`;
    
    const response = await P2P_API.get(url);
    console.log('Raw completed trades response:', response.data);
    
    // Handle different response formats
    let backendTrades = [];
    if (Array.isArray(response.data)) {
      backendTrades = response.data;
    } else if (response.data?.data && Array.isArray(response.data.data)) {
      backendTrades = response.data.data;
    } else if (response.data?.trades && Array.isArray(response.data.trades)) {
      backendTrades = response.data.trades;
    } else {
      backendTrades = [];
    }
    
    // Map backend response to frontend Trade interface
    const mappedTrades: Trade[] = backendTrades.map((backendTrade: any) => ({
      id: backendTrade._id || backendTrade.tradeId || backendTrade.id,
      offerId: backendTrade.offerId,
      buyerId: backendTrade.buyerEmail || backendTrade.buyerId,
      sellerId: backendTrade.sellerEmail || backendTrade.sellerId,
      buyerEmail: backendTrade.buyerEmail,
      sellerEmail: backendTrade.sellerEmail,
      cryptoType: backendTrade.cryptoCurrency || backendTrade.cryptoType,
      paymentMethod: backendTrade.paymentMethod,
      amount: backendTrade.cryptoAmount || backendTrade.amount || 0,
      price: backendTrade.pricePerUnit || backendTrade.price || 0,
      totalAmount: backendTrade.fiatAmount || backendTrade.totalAmount || 0,
      status: backendTrade.status ? backendTrade.status.toLowerCase() : 'completed',
      createdAt: backendTrade.createdAt,
      updatedAt: backendTrade.updatedAt || backendTrade.createdAt,
      paymentDetails: backendTrade.paymentDetails?.transactionId || backendTrade.paymentDetails,
      cryptoTransactionHash: backendTrade.cryptoTransactionHash,
      disputeReason: backendTrade.disputeReason,
      disputeStatus: backendTrade.disputeStatus,
    }));
    
    console.log('Mapped completed trades:', mappedTrades.length);
    
    return {
      data: mappedTrades,
      pagination: {
        total: mappedTrades.length,
        page: 1,
        limit: mappedTrades.length,
        totalPages: 1,
      },
    };
  } catch (error: any) {
    console.error('Failed to fetch completed trades:', error);
    // Return empty array instead of throwing to prevent UI errors
    return {
      data: [],
      pagination: {
        total: 0,
        page: 1,
        limit: 0,
        totalPages: 0,
      },
    };
  }
};

// Escrow Flow APIs
export const sendPayment = async (tradeId: string, paymentDetails: string) => {
  try {
    const response = await P2P_API.post('/escrow/send-payment', {
      tradeId,
      paymentDetails,
    });
    return response.data;
  } catch (error: any) {
    console.error('Failed to send payment:', error);
    throw error.response?.data || error;
  }
};

export const confirmPayment = async (tradeId: string) => {
  try {
    console.log('Confirming payment for trade:', tradeId);
    const response = await P2P_API.post(`/trades/${tradeId}/confirm-payment`);
    return response.data;
  } catch (error: any) {
    console.error('Failed to confirm payment:', error);
    throw error.response?.data || error;
  }
};

export const sendCrypto = async (tradeId: string, transactionHash: string) => {
  try {
    console.log('Sending crypto to escrow for trade:', tradeId);
    const response = await P2P_API.post(`/trades/${tradeId}/send-crypto`, {
      transactionHash,
    });
    return response.data;
  } catch (error: any) {
    console.error('Failed to send crypto:', error);
    throw error.response?.data || error;
  }
};

export const confirmCrypto = async (tradeId: string) => {
  try {
    console.log('Confirming crypto for trade:', tradeId);
    const response = await P2P_API.post(`/trades/${tradeId}/confirm-crypto`);
    return response.data;
  } catch (error: any) {
    console.error('Failed to confirm crypto:', error);
    throw error.response?.data || error;
  }
};

export const releaseEscrow = async (tradeId: string) => {
  try {
    console.log('Releasing escrow for trade:', tradeId);
    const response = await P2P_API.post(`/trades/${tradeId}/release-escrow`);
    return response.data;
  } catch (error: any) {
    console.error('Failed to release escrow:', error);
    throw error.response?.data || error;
  }
};

// Auto-escrow functions for seamless trading
export const autoSendCryptoToEscrow = async (tradeId: string, transactionHash?: string) => {
  try {
    console.log('Automatically sending crypto to escrow for trade:', tradeId);
    const response = await P2P_API.post('/escrow/send-crypto', {
      tradeId,
      transactionHash: transactionHash || 'auto-generated-hash-' + Date.now(),
      autoEscrow: true
    });
    console.log('Crypto sent to escrow:', response.data);
    return response.data;
  } catch (error: any) {
    console.error('Failed to auto-send crypto to escrow:', error);
    throw error.response?.data || error;
  }
};

export const autoConfirmAndRelease = async (tradeId: string) => {
  try {
    console.log('Auto-confirming and releasing escrow for trade:', tradeId);
    
    // Step 1: Get trade details to determine asset release
    const tradeResponse = await P2P_API.get(`/trades/${tradeId}`);
    const trade = tradeResponse.data.data;
    
    console.log('Trade details for release:', trade);
    
    // Step 2: Confirm payment
    const paymentResponse = await P2P_API.post('/escrow/confirm-payment', {
      tradeId,
      autoConfirm: true
    });
    console.log('Payment confirmed:', paymentResponse.data);
    
    // Step 3: Confirm crypto
    const cryptoResponse = await P2P_API.post('/escrow/confirm-crypto', {
      tradeId,
      autoConfirm: true
    });
    console.log('Crypto confirmed:', cryptoResponse.data);
    
    // Step 4: Release escrow
    const releaseResponse = await P2P_API.post('/escrow/release', {
      tradeId,
      autoRelease: true
    });
    console.log('Escrow released:', releaseResponse.data);
    
    // Step 5: Release assets from escrow to final recipients
    try {
      console.log('Releasing assets from escrow to final recipients...');
      
      // Release crypto to buyer
      const cryptoRelease = await releaseAssetsFromEscrow(
        'escrow@indexx.exchange', // escrow wallet
        trade.buyerEmail, // buyer email
        trade.cryptoAmount,
        trade.cryptoCurrency,
        trade.cryptoNetwork || '',
        tradeId
      );
      console.log('Crypto released to buyer:', cryptoRelease);
      
      // Release payment to seller (if applicable)
      if (trade.fiatAmount && trade.fiatAmount > 0) {
        const paymentRelease = await releaseAssetsFromEscrow(
          'escrow@indexx.exchange', // escrow wallet
          trade.sellerEmail, // seller email
          trade.fiatAmount,
          trade.fiatCurrency || 'USD',
          '', // fiat doesn't have network
          tradeId
        );
        console.log('Payment released to seller:', paymentRelease);
      }
      
      return {
        success: true,
        message: 'Trade completed automatically - payment and crypto confirmed, escrow released, assets transferred',
        paymentConfirmed: paymentResponse.data,
        cryptoConfirmed: cryptoResponse.data,
        escrowReleased: releaseResponse.data,
        assetRelease: {
          cryptoRelease,
          paymentRelease: trade.fiatAmount ? 'Payment released to seller' : 'No payment to release'
        }
      };
    } catch (assetReleaseError: any) {
      console.error('Failed to release assets from escrow:', assetReleaseError);
      
      return {
        success: true,
        message: 'Trade completed but asset release failed - manual intervention required',
        paymentConfirmed: paymentResponse.data,
        cryptoConfirmed: cryptoResponse.data,
        escrowReleased: releaseResponse.data,
        assetReleaseError: assetReleaseError.message
      };
    }
  } catch (error: any) {
    console.error('Failed to auto-confirm and release:', error);
    throw error.response?.data || error;
  }
};

// Function to handle seller joining buy offer (send crypto to escrow)
export const sellerJoinBuyOffer = async (tradeId: string) => {
  try {
    console.log('Seller joining buy offer - sending crypto to escrow for trade:', tradeId);
    
    // Send crypto to escrow
    const cryptoResponse = await autoSendCryptoToEscrow(tradeId);
    console.log('Crypto sent to escrow by seller:', cryptoResponse);
    
    return {
      success: true,
      message: 'Crypto automatically sent to escrow. Buyer will send payment.',
      escrowStatus: 'crypto_sent'
    };
  } catch (error: any) {
    console.error('Failed to send crypto to escrow:', error);
    throw error.response?.data || error;
  }
};

// Function to transfer assets to escrow wallet
export const transferAssetsToEscrow = async (
  fromEmail: string,
  toEmail: string,
  amount: number,
  coin: string,
  network: string = '',
  tradeId: string
) => {
  try {
    console.log('Transferring assets to escrow:', {
      fromEmail,
      toEmail,
      amount,
      coin,
      network,
      tradeId
    });
    
    // Use the existing wallet transfer API
    const transferResponse = await createSendTxByEmail(
      toEmail, // escrow wallet email
      amount,
      fromEmail,
      coin,
      network
    );
    
    console.log('Assets transferred to escrow:', transferResponse);
    
    return {
      success: true,
      transferResponse,
      message: `Successfully transferred ${amount} ${coin} to escrow`
    };
  } catch (error: any) {
    console.error('Failed to transfer assets to escrow:', error);
    throw error.response?.data || error;
  }
};

// Function to release assets from escrow to final recipient
export const releaseAssetsFromEscrow = async (
  escrowEmail: string,
  recipientEmail: string,
  amount: number,
  coin: string,
  network: string = '',
  tradeId: string
) => {
  try {
    console.log('Releasing assets from escrow:', {
      escrowEmail,
      recipientEmail,
      amount,
      coin,
      network,
      tradeId
    });
    
    // Transfer from escrow to final recipient
    const releaseResponse = await createSendTxByEmail(
      recipientEmail,
      amount,
      escrowEmail,
      coin,
      network
    );
    
    console.log('Assets released from escrow:', releaseResponse);
    
    return {
      success: true,
      releaseResponse,
      message: `Successfully released ${amount} ${coin} to recipient`
    };
  } catch (error: any) {
    console.error('Failed to release assets from escrow:', error);
    throw error.response?.data || error;
  }
};

// Function to complete trade directly (bypass escrow APIs)
export const completeTradeDirectly = async (tradeId: string) => {
  try {
    console.log('Completing trade directly:', tradeId);
    
    // Get trade details
    const tradeResponse = await P2P_API.get(`/trades/${tradeId}`);
    const trade = tradeResponse.data.data;
    
    console.log('Trade details for completion:', trade);
    
    // Transfer assets directly between users (bypassing escrow)
    const buyerEmail = trade.buyerEmail;
    const sellerEmail = trade.sellerEmail;
    
    // Transfer crypto from seller to buyer
    const cryptoTransfer = await createSendTxByEmail(
      buyerEmail, // to buyer
      trade.cryptoAmount,
      sellerEmail, // from seller
      trade.cryptoCurrency,
      trade.cryptoNetwork || ''
    );
    console.log('Crypto transferred directly to buyer:', cryptoTransfer);
    
    // Transfer payment from buyer to seller
    const paymentTransfer = await createSendTxByEmail(
      sellerEmail, // to seller
      trade.fiatAmount,
      buyerEmail, // from buyer
      trade.fiatCurrency || 'USD',
      '' // fiat doesn't have network
    );
    console.log('Payment transferred directly to seller:', paymentTransfer);
    
    return {
      success: true,
      message: 'Trade completed directly - assets transferred between users',
      cryptoTransfer,
      paymentTransfer,
      tradeId
    };
  } catch (error: any) {
    console.error('Failed to complete trade directly:', error);
    throw error.response?.data || error;
  }
};

// Function to refresh wallet data after escrow operations
export const refreshWalletData = async (userEmail: string) => {
  try {
    console.log('Refreshing wallet data for user:', userEmail);
    
    // Import getUserWallets from api service
    const { getUserWallets } = await import('./api');
    
    // Actually fetch and refresh wallet data
    const walletResponse = await getUserWallets(userEmail);
    console.log('Wallet data refreshed successfully:', walletResponse);
    
    // Store a flag in localStorage to trigger dashboard refresh
    localStorage.setItem('p2p_wallet_refresh_needed', Date.now().toString());
    
    return {
      success: true,
      message: 'Wallet data refreshed successfully',
      timestamp: new Date().toISOString(),
      walletData: walletResponse
    };
  } catch (error: any) {
    console.error('Failed to refresh wallet data:', error);
    throw error.response?.data || error;
  }
};

// User Management APIs
export const getWallets = async () => {
  try {
    const response = await P2P_API.get('/wallets');
    return response.data;
  } catch (error: any) {
    console.error('Failed to fetch wallets:', error);
    throw error.response?.data || error;
  }
};

export const fundWallet = async (cryptoType: string, amount: number) => {
  try {
    const response = await P2P_API.post('/wallets/fund', {
      cryptoType,
      amount,
    });
    return response.data;
  } catch (error: any) {
    console.error('Failed to fund wallet:', error);
    throw error.response?.data || error;
  }
};

export const createWallet = async (cryptoType: string) => {
  try {
    const response = await P2P_API.post('/wallets/create', {
      cryptoType,
    });
    return response.data;
  } catch (error: any) {
    console.error('Failed to create wallet:', error);
    throw error.response?.data || error;
  }
};

// Dispute Management APIs
export const createDispute = async (disputeData: {
  tradeId: string;
  reason: string;
  description: string;
  evidence?: string[];
}) => {
  try {
    const response = await P2P_API.post('/disputes', disputeData);
    return response.data;
  } catch (error: any) {
    console.error('Failed to create dispute:', error);
    throw error.response?.data || error;
  }
};

export const getDisputes = async (filters?: {
  status?: string;
  page?: number;
  limit?: number;
}) => {
  try {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, value.toString());
        }
      });
    }
    
    const response = await P2P_API.get(`/disputes?${params.toString()}`);
    return response.data;
  } catch (error: any) {
    console.error('Failed to fetch disputes:', error);
    throw error.response?.data || error;
  }
};

// Rating APIs
export const createRating = async (ratingData: {
  toUser: string;
  rating: number;
  comment: string;
  tradeId: string;
}) => {
  try {
    const response = await P2P_API.post('/ratings', ratingData);
    return response.data;
  } catch (error: any) {
    console.error('Failed to create rating:', error);
    throw error.response?.data || error;
  }
};

export const getRatings = async (userEmail: string) => {
  try {
    const response = await P2P_API.get(`/ratings/${userEmail}`);
    return response.data;
  } catch (error: any) {
    console.error('Failed to fetch ratings:', error);
    throw error.response?.data || error;
  }
};

// User Profile APIs
export const getUserProfile = async (userEmail: string) => {
  try {
    // Check if backend is available
    const backendAvailable = await isBackendAvailable();
    console.log('Backend available:', backendAvailable);
    
    if (!backendAvailable) {
      console.log('Using mock data');
      // Return mock data
      return { data: MOCK_USER_PROFILE };
    }
    
    console.log('Using real backend data');
    // Use real backend
    const response = await P2P_API.get(`/profile/${userEmail}`);
    return response.data;
  } catch (error: any) {
    console.error('Failed to fetch user profile:', error);
    throw error.response?.data || error;
  }
};

// User-specific offer management APIs (requires authentication)
export const createOfferWithAssetWallet = async (offerData: {
  type: 'BUY' | 'SELL';
  baseToken: string;      // Currency user will RECEIVE
  quoteToken: string;    // Currency user will SEND
  price: number;
  minAmount: number;
  maxAmount: number;
  availableAmount: number;
}) => {
  try {
    const response = await P2P_API.post('/user/offers', offerData);
    return response.data;
  } catch (error: any) {
    console.error('Failed to create offer with asset wallet:', error);
    throw error.response?.data || error;
  }
};

export const userUpdateOffer = async (offerId: string, updateData: {
  price?: number;
  amount?: number;
  minAmount?: number;
  maxAmount?: number;
  terms?: string;
  autoReply?: string;
  status?: 'active' | 'paused' | 'deleted';
}) => {
  try {
    console.log(`[UPDATE] Calling PUT /user/offers/${offerId}`, updateData);
    const response = await P2P_API.put(`/user/offers/${offerId}`, updateData);
    console.log(`[UPDATE] Success:`, response.data);
    return response.data;
  } catch (error: any) {
    console.error('Failed to update offer:', error);
    throw error.response?.data || error;
  }
};

export const userDeleteOffer = async (offerId: string) => {
  try {
    console.log(`[DELETE] Calling DELETE /user/offers/${offerId}`);
    const response = await P2P_API.delete(`/user/offers/${offerId}`);
    console.log(`[DELETE] Success:`, response.data);
    return response.data;
  } catch (error: any) {
    console.error('Failed to delete offer:', error);
    throw error.response?.data || error;
  }
};

export const userPauseOffer = async (offerId: string) => {
  try {
    console.log(`[PAUSE/RESUME] Calling POST /user/offers/${offerId}/pause`);
    const response = await P2P_API.post(`/user/offers/${offerId}/pause`);
    console.log(`[PAUSE/RESUME] Success:`, response.data);
    return response.data;
  } catch (error: any) {
    console.error('Failed to pause/resume offer:', error);
    throw error.response?.data || error;
  }
};

export const userListEscrows = async (offerId: string) => {
  try {
    const response = await P2P_API.get(`/user/offers/${offerId}/escrows`);
    return response.data;
  } catch (error: any) {
    console.error('Failed to list escrows:', error);
    throw error.response?.data || error;
  }
};

// Get user's own offers using the dedicated endpoint
export const getUserOffers = async (filters?: {
  status?: 'active' | 'paused' | 'deleted';
  type?: 'buy' | 'sell' | 'BUY' | 'SELL';
  limit?: number;
  offset?: number;
}) => {
  try {
    // Don't send status filter to backend - we'll filter on frontend
    // Backend might use different status format (Active vs active)
    const params = new URLSearchParams();
    if (filters?.type) {
      const type = filters.type.toLowerCase();
      params.append('type', type);
    }
    if (filters?.limit) params.append('limit', filters.limit.toString());
    if (filters?.offset) params.append('offset', filters.offset.toString());

    const url = `/user/offers${params.toString() ? `?${params.toString()}` : ''}`;
    console.log(`[GET] Calling GET ${url}`);
    console.log('Fetching user offers from:', url);
    const response = await P2P_API.get(url);
    console.log(`[GET] Success - User offers raw response:`, response);
    console.log('Response data:', response.data);
    
    // Handle different response structures
    let rawOffers = [];
    if (Array.isArray(response.data)) {
      rawOffers = response.data;
    } else if (response.data?.data && Array.isArray(response.data.data)) {
      rawOffers = response.data.data;
    } else if (response.data?.offers && Array.isArray(response.data.offers)) {
      rawOffers = response.data.offers;
    } else if (response.data && typeof response.data === 'object') {
      // Try to find array in response
      rawOffers = Object.values(response.data).find((val: any) => Array.isArray(val)) as any[] || [];
    }
    
    console.log('Raw offers array:', rawOffers);
    console.log('Number of raw offers:', rawOffers.length);
    
    // Map backend offers to frontend Offer interface format
    const mappedOffers: Offer[] = rawOffers.map((offer: any) => {
      // Handle crypto type - could be baseToken, quoteToken, cryptoType, cryptoCurrency, etc.
      const cryptoType = offer.baseToken || offer.quoteToken || offer.cryptoType || offer.cryptoCurrency || 'UNKNOWN';
      
      // Handle offer type - normalize to lowercase
      const offerType = offer.type || offer.offerType || 'buy';
      const normalizedType = typeof offerType === 'string' ? offerType.toLowerCase() : 'buy';
      
      // Determine crypto type based on offer type
      // For BUY: baseToken is what user receives (crypto), quoteToken is what user sends (USD)
      // For SELL: baseToken is what user receives (USD), quoteToken is what user sends (crypto)
      let displayCryptoType = cryptoType;
      if (normalizedType === 'buy') {
        displayCryptoType = offer.baseToken || cryptoType;
      } else if (normalizedType === 'sell') {
        displayCryptoType = offer.quoteToken || cryptoType;
      }
      
      // Handle payment methods
      let paymentMethodsArray: string[] = [];
      if (offer.paymentMethods && Array.isArray(offer.paymentMethods)) {
        paymentMethodsArray = offer.paymentMethods;
      } else if (offer.paymentMethod) {
        paymentMethodsArray = [offer.paymentMethod];
      } else {
        paymentMethodsArray = ['Asset Wallet']; // Default for user offers
      }
      
      const primaryPaymentMethod = paymentMethodsArray[0] || 'Asset Wallet';
      
      // Map the offer
      return {
        id: offer.offerId || offer._id || offer.id,
        userId: offer._id || offer.userId || offer.creatorId,
        userEmail: offer.creatorEmail || offer.userEmail || localStorage.getItem('user') || '',
        username: (offer.creatorEmail || offer.userEmail || '').split('@')[0] || 'Trader',
        cryptoType: displayCryptoType,
        paymentMethod: primaryPaymentMethod,
        paymentMethods: paymentMethodsArray,
        price: offer.price || offer.pricePerUnit || 0,
        amount: offer.availableAmount || offer.amount || 0,
        minAmount: offer.minAmount || 0,
        maxAmount: offer.maxAmount || 0,
        terms: offer.terms || 'No terms specified',
        autoReply: offer.autoReply || '',
        completionRate: offer.completionRate || 100,
        totalTrades: offer.totalTrades || 0,
        createdAt: offer.createdAt || offer.created_at || new Date().toISOString(),
        updatedAt: offer.updatedAt || offer.updated_at || new Date().toISOString(),
        status: offer.status ? offer.status.toLowerCase() : 'active',
        type: normalizedType,
        fiatCurrency: offer.fiatCurrency || 'USD',
        paymentMethodType: offer.paymentMethodType || 'INTERNAL_USD',
      } as Offer;
    });
    
    console.log('Mapped offers (before status filter):', mappedOffers);
    console.log('All offer statuses:', mappedOffers.map(o => ({ id: o.id, status: o.status })));
    
    // Filter by status on frontend (case-insensitive)
    let filteredOffers = mappedOffers;
    if (filters?.status) {
      const targetStatus = filters.status.toLowerCase();
      console.log(`Filtering for status: "${targetStatus}"`);
      filteredOffers = mappedOffers.filter((offer: Offer) => {
        const offerStatus = (offer.status || 'active').toLowerCase();
        const matches = offerStatus === targetStatus;
        if (!matches) {
          console.log(`Offer ${offer.id} status "${offerStatus}" does not match "${targetStatus}"`);
        }
        return matches;
      });
      console.log(`Filtered offers for status "${filters.status}":`, filteredOffers.length);
      console.log('Filtered offer IDs:', filteredOffers.map(o => o.id));
    }
    
    console.log('Final mapped offers:', filteredOffers);
    
    return {
      data: filteredOffers,
      success: true
    };
  } catch (error: any) {
    console.error('Failed to fetch user offers:', error);
    console.error('Error details:', {
      message: error?.message,
      response: error?.response?.data,
      status: error?.response?.status,
      statusText: error?.response?.statusText
    });
    
    // Create a more descriptive error
    const errorMessage = error?.response?.data?.message || 
                        error?.response?.data?.error ||
                        error?.message ||
                        'Failed to get user offers';
    
    const enhancedError = new Error(errorMessage);
    (enhancedError as any).response = error?.response;
    throw enhancedError;
  }
};

export default P2P_API;
